import type { Component } from 'solid-js'
import { createResource, createSignal, Show, createMemo } from 'solid-js'
import { useCorisSubscribers } from '../../shared/providers/coris/coris-subscribers.provider'
import { useUser } from '../../shared/providers/user-provider'
import { SubscriberSortBy } from '../../features/subscribers/domain/models/subscriber'
import { sticky } from '../../shared/directives/sticky'
import UserDirectoryTable from './UserDirectoryTable'
import UserDirectoryFilters from './UserDirectoryFilters'
import EmptyState from '../shared/components/EmptyState'
import { Trans, useTransContext } from '@mbarzda/solid-i18next'

// Prevent tree-shaking of directive
sticky

const PAGE_SIZE = 15

const UserDirectory: Component = () => {
  const [t] = useTransContext()
  const [isStuck, setIsStuck] = createSignal(false)
  const [page, setPage] = createSignal(1)
  const [searchTerm, setSearchTerm] = createSignal('')
  const [fuzzy, setFuzzy] = createSignal(false)
  const [sortBy, setSortBy] = createSignal<SubscriberSortBy>(SubscriberSortBy.NAME)
  const [sortOrder, setSortOrder] = createSignal<'asc' | 'desc'>('asc')

  const [selectedCountries, setSelectedCountries] = createSignal<string[]>([])
  const [selectedRegions, setSelectedRegions] = createSignal<string[]>([])
  const [selectedPlans, setSelectedPlans] = createSignal<string[]>([])
  const [selectedLevels, setSelectedLevels] = createSignal<string[]>([])
  const [selectedPreviousLevels, setSelectedPreviousLevels] = createSignal<string[]>([])
  const [selectedStatuses, setSelectedStatuses] = createSignal<string[]>([])

  const { getSubscribers, getSubscriberFilters } = useCorisSubscribers()
  const { organizationSlug } = useUser()

  const [filtersData] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return { organization }
    },
    async (params) => {
      if (!params) return null
      return await getSubscriberFilters.execute(params)
    }
  )

  const [subscribersData, { refetch: refetchSubscribers }] = createResource(
    () => {
      const organization = organizationSlug()
      if (!organization) return null
      return {
        search: searchTerm().trim() || null,
        fuzzy: searchTerm().trim() ? fuzzy() : false,
        country: selectedCountries().length > 0 ? selectedCountries() : null,
        region: selectedRegions().length > 0 ? selectedRegions() : null,
        subscriptionPlan: selectedPlans().length > 0 ? selectedPlans() : null,
        subscriptionLevel: selectedLevels().length > 0 ? selectedLevels() : null,
        previousSubscriptionLevel: selectedPreviousLevels().length > 0 ? selectedPreviousLevels() : null,
        subscriptionStatuses: selectedStatuses().length > 0 ? selectedStatuses() : null,
        sortBy: sortBy(),
        sortOrder: sortOrder(),
        offset: (page() - 1) * PAGE_SIZE,
        limit: PAGE_SIZE,
        organization
      }
    },
    async (params) => {
      if (!params) return null
      return await getSubscribers.execute(params)
    }
  )

  const handleSortChange = (column: SubscriberSortBy, direction: 'asc' | 'desc') => {
    setSortBy(column)
    setSortOrder(direction)
    setPage(1)
  }

  const handleSearch = (value: string) => {
    setSearchTerm(value)
    setPage(1)
  }

  const handleFuzzyChange = (value: boolean) => {
    setFuzzy(value)
    setPage(1)
  }

  const handleFilterChange = (
    type: 'country' | 'region' | 'plan' | 'level' | 'previousLevel' | 'status',
    values: string[]
  ) => {
    switch (type) {
      case 'country':
        setSelectedCountries(values)
        break
      case 'region':
        setSelectedRegions(values)
        break
      case 'plan':
        setSelectedPlans(values)
        break
      case 'level':
        setSelectedLevels(values)
        break
      case 'previousLevel':
        setSelectedPreviousLevels(values)
        break
      case 'status':
        setSelectedStatuses(values)
        break
    }
    setPage(1)
  }

  const handlePageChange = (newPage: number) => {
    setPage(newPage)
  }

  const buildExportParams = (): URLSearchParams | null => {
    const organization = organizationSlug()
    if (!organization) return null

    const params = new URLSearchParams()
    params.set('organization', organization)
    if (searchTerm().trim()) {
      params.set('search', searchTerm().trim())
      if (fuzzy()) params.set('fuzzy', 'true')
    }
    for (const c of selectedCountries()) params.append('country', c)
    for (const r of selectedRegions()) params.append('region', r)
    for (const p of selectedPlans()) params.append('subscription_plan', p)
    for (const l of selectedLevels()) params.append('subscription_level', l)
    for (const pl of selectedPreviousLevels()) params.append('previous_subscription_level', pl)
    for (const st of selectedStatuses()) params.append('subscription_status', st)
    return params
  }

  const isLoading = createMemo(() => subscribersData.loading || subscribersData.state === 'unresolved')
  const hasError = createMemo(() => !!subscribersData.error)
  const hasData = createMemo(() => {
    const data = subscribersData()
    return data && data.items.length > 0
  })

  return (
    <div class="flex w-full flex-col p-6">
      {/* Sticky filter bar — full-width background, content constrained */}
      <div
        use:sticky={setIsStuck}
        class="sticky top-0 z-10 -mx-6 px-6 py-6 transition-colors duration-200"
        classList={{
          'bg-white border-b border-gray-200': isStuck(),
          'bg-transparent': !isStuck()
        }}
      >
        <div class="mx-auto w-full max-w-[1280px]">
          <UserDirectoryFilters
            searchTerm={searchTerm()}
            fuzzy={fuzzy()}
            filters={
              filtersData() ?? {
                subscription_sources: [],
                subscription_plans: [],
                subscription_levels: [],
                previous_subscription_levels: [],
                subscription_statuses: [],
                regions: {}
              }
            }
            selectedCountries={selectedCountries()}
            selectedRegions={selectedRegions()}
            selectedPlans={selectedPlans()}
            selectedLevels={selectedLevels()}
            selectedPreviousLevels={selectedPreviousLevels()}
            selectedStatuses={selectedStatuses()}
            onSearch={handleSearch}
            onFuzzyChange={handleFuzzyChange}
            onFilterChange={handleFilterChange}
            buildExportParams={buildExportParams}
          />
        </div>
      </div>

      {/* Content — constrained */}
      <div class="mx-auto flex w-full max-w-[1280px] flex-col gap-8 pt-6">
        {/* Error state */}
        <Show when={hasError() && !isLoading()}>
          <div class="flex flex-col items-center justify-center gap-3 rounded-lg border border-red-200 bg-red-50 py-12">
            <p class="text-sm text-red-700">{t('ls_generic_error_message')}</p>
            <button
              type="button"
              onClick={() => refetchSubscribers()}
              class="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-red-700"
            >
              <Trans key={'ls_generic_retry'} />
            </button>
          </div>
        </Show>

        {/* Table */}
        <Show
          when={!hasError() && (hasData() || isLoading())}
          fallback={
            <Show when={!hasError()}>
              <EmptyState />
            </Show>
          }
        >
          <UserDirectoryTable
            subscribers={subscribersData()?.items ?? []}
            totalCount={subscribersData()?.total ?? 0}
            page={page}
            pageSize={PAGE_SIZE}
            setPage={handlePageChange}
            sortBy={sortBy()}
            sortOrder={sortOrder()}
            onSort={handleSortChange}
            isLoading={isLoading()}
          />
        </Show>
      </div>
    </div>
  )
}

export default UserDirectory
